/**
 * The tick loop — spec §8.
 *
 *   step(state, commands) => state
 *
 * Spec §8 is explicit that determinism depends on this ordering never varying.
 * Every one of the nine steps is present and called from the start, even while
 * its body is empty, so that later phases fill bodies in rather than inserting
 * steps — an inserted step is exactly the kind of change that silently reorders
 * effects and invalidates every prior fixture.
 *
 * The function takes a state, works on a private deep copy, and returns it. The
 * caller's state is never mutated.
 */

import {
  advanceConstruction as advanceCityConstruction,
  advanceDemolition as advanceCityDemolition,
  plotAt,
} from './city';
import { advanceColony, requestColonists } from './colony';
import { advanceMarket, setMarketOrder } from './market';
import { assertKnownCommand, type Command } from './commands';
import { advanceDrones as advanceDronePhases } from './drones';
import {
  advanceBuildQueue as advanceQueue,
  cancelQueued,
  demolish,
  queueBuild,
  recordProduction,
  reorderQueue,
} from './queue';
import {
  emptyPlan,
  planDissociation,
  planIntake,
  planSynthesis,
  resolvePowerAndApply,
  type FactoryPlan,
} from './factory';
import { hexKey } from './hex';
import { cloneState } from './state';
import { advanceAssembly, cancelAssemblyOrder, orderUnit } from './units';
import type { GameState } from './types';

/**
 * Step 0 — apply player commands.
 *
 * Not numbered in spec §8, which describes only the simulation steps. Commands
 * are applied before them: they are the player's intent for this tick, and
 * applying them first means an order issued at tick N takes effect during tick
 * N rather than N+1.
 */
function applyCommands(state: GameState, commands: Command[]): void {
  for (const command of commands) {
    assertKnownCommand(command);
    switch (command.type) {
      case 'assignDrone': {
        const drone = state.drones.find((d) => d.id === command.droneId);
        if (drone === undefined) break;
        // Reject an assignment to a hex that does not exist rather than
        // silently parking the drone against a null hex forever.
        if (state.planet.hexes[hexKey(command.hex)] === undefined) break;
        drone.assignedHex = { q: command.hex.q, r: command.hex.r };
        break;
      }

      case 'setDroneAutoReassign': {
        const drone = state.drones.find((d) => d.id === command.droneId);
        if (drone === undefined) break;
        drone.autoReassign = command.autoReassign;
        break;
      }

      case 'buildUnit': {
        // An order for the factory's assembly bay, not an instant purchase.
        orderUnit(state, {
          unit: command.unit,
          ...(command.class === undefined ? {} : { class: command.class }),
        });
        break;
      }

      case 'cancelAssembly':
        cancelAssemblyOrder(state, command.index);
        break;

      case 'setSynthesis': {
        const setting = state.factory.synthesis[command.good];
        setting.enabled = command.enabled;
        setting.priority = command.priority;
        // Omitted leaves the target alone, so a panel that only toggles a
        // recipe cannot silently wipe a target the player set.
        if (command.target !== undefined) {
          setting.target = Math.max(0, Math.floor(command.target));
        }
        break;
      }

      case 'queueBuild':
        queueBuild(state, command.plots, command.building);
        break;

      case 'reorderQueue':
        reorderQueue(state, command.from, command.to);
        break;

      case 'cancelQueued':
        cancelQueued(state, command.index);
        break;

      case 'demolish':
        demolish(state, command.plot);
        break;

      case 'setBuildingEnabled': {
        const plot = plotAt(state, command.plot);
        // Only a finished building can run. A half-built one has nothing to
        // switch on.
        if (plot === null || plot.state !== 'complete') break;
        plot.enabled = command.enabled;
        break;
      }

      case 'requestColonists':
        requestColonists(state);
        break;

      case 'setMarketOrder':
        setMarketOrder(state, command.element, command.mode, command.threshold);
        break;
    }
  }
}

/** Step 1 — advance drone phases. spec §8.1. */
function advanceDrones(state: GameState): void {
  advanceDronePhases(state);
}

/**
 * Steps 2, 3 and 4 PLAN ONLY — they add intent to `plan` and mutate no state.
 * Step 5 is the single place inventory changes, and it always applies the
 * throttle. Structuring it this way is what keeps spec §8's "compute intended
 * demand, then the ratio, then apply it uniformly" true by construction rather
 * than by remembering to do it.
 */

/** Step 2 — atmospheric N2 intake, subject to energy. spec §8.2 */
function atmosphericIntake(state: GameState, plan: FactoryPlan): void {
  planIntake(state, plan);
}

/** Step 3 — dissociate minerals into elements. spec §8.3 */
function dissociate(state: GameState, plan: FactoryPlan): void {
  planDissociation(state, plan);
}

/** Step 4 — run active synthesis recipes. spec §8.4 */
function synthesize(state: GameState, plan: FactoryPlan): void {
  planSynthesis(state, plan);
}

/**
 * Step 5 — resolve power. spec §8.5.
 *
 * Steps 2-4 and this one are circular: power depends on demand, demand depends
 * on rates. Resolved in a single pass — intended demand, then one ratio, then
 * uniform application. No iteration.
 */
function resolvePower(state: GameState, plan: FactoryPlan): void {
  resolvePowerAndApply(state, plan);
}

/** Step 6 — advance the build queue head. spec §8.6, §9 */
function advanceBuildQueue(state: GameState, plan: FactoryPlan): void {
  // Fold this tick's synthesis into the production window first, so the starved
  // indicator's projected wait reflects income including the tick just run.
  recordProduction(state, plan.produced);
  advanceQueue(state);
}

/** Step 7 — advance construction. spec §8.7 */
function advanceConstruction(state: GameState): void {
  advanceCityConstruction(state);
}

/** Step 8 — advance demolition, refunding on completion. spec §8.8 */
function advanceDemolition(state: GameState): void {
  advanceCityDemolition(state);
}

/**
 * Advance the simulation by exactly one tick.
 *
 * Pure with respect to `state`: the input is never mutated, and the same
 * (state, commands) pair always produces the same output.
 */
export function step(state: GameState, commands: Command[] = []): GameState {
  const next = cloneState(state);

  applyCommands(next, commands);

  const plan = emptyPlan();

  advanceDrones(next);
  atmosphericIntake(next, plan);
  dissociate(next, plan);
  synthesize(next, plan);
  resolvePower(next, plan);
  advanceBuildQueue(next, plan);
  advanceConstruction(next);
  advanceDemolition(next);

  // The factory's own assembly bay, after the city's construction — a unit
  // finished this tick is available from the next one.
  advanceAssembly(next);

  // Not one of spec §8's nine steps — the colonist layer came later. It runs
  // last so a building finished this tick serves the colony from the next one,
  // rather than retroactively.
  advanceColony(next);

  // Trade settles after production, so a tick sells what it actually made and
  // buys against the shortfall that is actually left.
  advanceMarket(next);

  // Step 9 in spec §8 is "emit state snapshot to UI". That is the return value;
  // the sim does not push, and it does not know the UI exists.
  next.tick += 1;

  return next;
}

/**
 * Run many ticks. Used by the determinism harness and by the Phase 8
 * fast-forward tooling.
 */
export function runTicks(state: GameState, ticks: number): GameState {
  let current = state;
  for (let i = 0; i < ticks; i++) current = step(current);
  return current;
}
